/**
 * Per-account check-start-time helpers.
 *
 * The check start gates when a broker account's per-minute checks
 * (SL/WAT/ORB/RE/TP/TSL/MTM/broker-sync) begin. Range [09:16, 12:00]; an
 * account with no configured value defaults to 09:16. Bounds mirror the
 * backend (`CHECK_START_GATE` ← MarketHours.START / CheckStartGate.MAX).
 */
import { CHECK_START_GATE } from '../constants/tradingConstants';

const pad2 = (n: number): string => String(n).padStart(2, '0');

const toMinutes = (hour: number, minute: number): number => hour * 60 + minute;

const MIN_MINUTES = toMinutes(CHECK_START_GATE.MIN_HOUR, CHECK_START_GATE.MIN_MINUTE);
const MAX_MINUTES = toMinutes(CHECK_START_GATE.MAX_HOUR, CHECK_START_GATE.MAX_MINUTE);

/**
 * Effective "HH:MM" for an account, defaulting to 09:16 when EITHER field is
 * unset — matching the backend `resolve_check_start`, which treats a half-set
 * row as fully unset. In practice both fields are written together.
 */
export const formatCheckStart = (
  hour?: number | null,
  minute?: number | null,
): string => {
  if (hour == null || minute == null) {
    return `${pad2(CHECK_START_GATE.DEFAULT_HOUR)}:${pad2(CHECK_START_GATE.DEFAULT_MINUTE)}`;
  }
  return `${pad2(hour)}:${pad2(minute)}`;
};

/** Parse an "HH:MM" string → {hour, minute}, or null if malformed. */
export const parseCheckStart = (
  value: string,
): { hour: number; minute: number } | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec((value ?? '').trim());
  if (!m) return null;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
  if (minute < 0 || minute > 59 || hour < 0 || hour > 23) return null;
  return { hour, minute };
};

/** True if (hour, minute) is within the allowed [09:16, 12:00] range (inclusive). */
export const isCheckStartInRange = (hour: number, minute: number): boolean => {
  const t = toMinutes(hour, minute);
  return t >= MIN_MINUTES && t <= MAX_MINUTES;
};

/** Human-readable range label for help text / errors, e.g. "09:16–12:00". */
export const CHECK_START_RANGE_LABEL = `${formatCheckStart(
  CHECK_START_GATE.MIN_HOUR,
  CHECK_START_GATE.MIN_MINUTE,
)}–${formatCheckStart(CHECK_START_GATE.MAX_HOUR, CHECK_START_GATE.MAX_MINUTE)}`;

interface CheckStartAccount {
  client_id: string;
  check_start_hour?: number;
  check_start_minute?: number;
}

export interface CheckStartUpdate {
  clientId: string;
  hour: number;
  minute: number;
}

/**
 * Bucket per-account "HH:MM" inputs into the accounts that need a PUT.
 *
 *   empty (cleared) or unchanged → skipped (there is no "clear to unset" — the
 *                                 default is 09:16, so empty means "don't touch")
 *   malformed / out-of-range     → `invalid` flag set (caller blocks + errors)
 *   a new valid in-range time    → queued in `updates`
 *
 * An empty field never blocks the save of OTHER changed accounts.
 */
export const bucketCheckStartUpdates = (
  accounts: CheckStartAccount[],
  values: Record<string, string>,
): { updates: CheckStartUpdate[]; invalid: boolean } => {
  const updates: CheckStartUpdate[] = [];
  let invalid = false;
  for (const a of accounts) {
    const raw = (values[a.client_id] ?? '').trim();
    const current = formatCheckStart(a.check_start_hour, a.check_start_minute);
    if (raw === '' || raw === current) continue;
    const parsed = parseCheckStart(raw);
    if (!parsed || !isCheckStartInRange(parsed.hour, parsed.minute)) {
      invalid = true;
      continue;
    }
    updates.push({ clientId: a.client_id, hour: parsed.hour, minute: parsed.minute });
  }
  return { updates, invalid };
};
